import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, MapPin } from "lucide-react";
import { IMAGES } from "@/data/images";

const LINES = ["FIT N FIX DIY.", "SORTED."];

const lineVariant = {
  hidden: { y: 80, clipPath: "inset(100% 0 0 0)" },
  show: (i) => ({
    y: 0,
    clipPath: "inset(0% 0 0 0)",
    transition: { duration: 0.9, delay: 0.35 + i * 0.18, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = ({ onQuote }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const badgeY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} id="top" className="relative pt-[110px] sm:pt-[130px] pb-16 blueprint overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center">
        {/* Left */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15 }}
            className="inline-flex items-center gap-2 border-2 border-[#0A1A4A] px-4 py-2 mb-8 bg-white"
            data-testid="hero-location-badge"
          >
            <MapPin size={16} className="text-[#2563EB]" />
            <span className="font-mono-accent text-[11px] tracking-widest uppercase">Swansea's Local Handyman</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.22 }}
            className="mb-4 font-mono-accent text-[11px] sm:text-xs tracking-widest uppercase text-[#2563EB]"
            data-testid="hero-tagline"
          >
            Specialists in light repairs &amp; maintenance — no job too small
          </motion.div>

          <h1 className="font-display font-black text-[#0A1A4A] leading-[0.92] text-5xl sm:text-7xl lg:text-8xl tracking-tight">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden">
                <motion.span
                  custom={i}
                  variants={lineVariant}
                  initial="hidden"
                  animate="show"
                  className={`inline-block ${i === 0 ? "text-[#2563EB]" : ""}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.7 }}
            className="mt-8 max-w-xl text-lg text-[#0A1A4A]/70 leading-relaxed"
          >
            From flat-pack builds to leaky taps, TV mounting to garden jobs — tell us what needs doing. There's a <strong className="text-[#0A1A4A]">minimum charge of £60 per job</strong>, with your final price quoted by email. No surprises.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={onQuote}
              data-testid="hero-quote-btn"
              className="group bg-[#0A1A4A] text-white font-display font-extrabold text-base uppercase tracking-wide px-8 py-4 border-2 border-[#0A1A4A] shadow-hard-blue hover:bg-[#2563EB] transition-colors flex items-center gap-3"
            >
              Get My Free Quote
              <ArrowDown size={20} className="group-hover:translate-y-1 transition-transform" />
            </button>
            <div className="font-mono-accent text-xs tracking-widest uppercase text-[#0A1A4A]/60">
              No obligation
            </div>
          </motion.div>
        </div>

        {/* Right image */}
        <div className="lg:col-span-5 relative">
          <motion.div
            style={{ y: imgY }}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1 }}
            className="relative"
          >
            <div className="border-2 border-[#0A1A4A] shadow-hard overflow-hidden" style={{ clipPath: "polygon(0 0, 100% 0, 100% 88%, 88% 100%, 0 100%)" }}>
              <img src={IMAGES.hero} alt="Local handyman with tool belt working on a home repair in Swansea" className="w-full h-[420px] lg:h-[540px] object-cover" />
            </div>
            <motion.div
              style={{ y: badgeY }}
              className="absolute -bottom-6 -left-6 bg-[#EAB308] border-2 border-[#0A1A4A] px-5 py-4 shadow-hard"
              data-testid="hero-fee-badge"
            >
              <div className="font-mono-accent text-[10px] tracking-widest uppercase text-[#0A1A4A]">Minimum Charge</div>
              <div className="font-display font-black text-3xl text-[#0A1A4A] leading-none">£60</div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
